import { useCallback, useMemo, useState } from 'react'
import {
  MAX_ITERATION_LENGTH,
  iterationsInRange,
  iterationsProblem,
  newIterationId,
  normaliseIterations,
  type IterationConfig,
} from '../data/iterations'
import { writeIterations } from '../data/settings'
import { shiftDayKey, todayKey } from '../lib/time'
import { formatDayLabel } from '../lib/format'
import { sameValue } from '../lib/equal'
import { SaveStatus } from './SaveStatus'
import { useAutoSave } from './useAutoSave'

interface Props {
  uid: string
  config: IterationConfig
  dayStartHour: number
}

/**
 * Iterations, however you run them: a repeating cadence that generates them, plus
 * any one-off that does not fit the pattern and replaces the generated one it
 * lands on.
 */
export function IterationsEditor({ uid, config, dayStartHour }: Props) {
  const [draft, setDraft] = useState(config)

  // Normalised, because a half-typed date or a cleared length would otherwise
  // ask for tens of thousands of iterations while you are still typing — and
  // because `config` arrives normalised, so this is the like-for-like comparison
  // that lets a landed write settle rather than staying forever "unsaved".
  const settled = useMemo(() => normaliseIterations(draft), [draft])
  const dirty = useMemo(() => !sameValue(settled, config), [settled, config])

  const write = useCallback((next: IterationConfig) => writeIterations(uid, next), [uid])
  const status = useAutoSave(draft, dirty, write, { validate: iterationsProblem })

  const today = todayKey(dayStartHour)
  const preview = useMemo(
    () => iterationsInRange(settled, shiftDayKey(today, -60), shiftDayKey(today, 60)),
    [settled, today],
  )

  const cadence = draft.cadence

  return (
    <div className="flex flex-col gap-3 p-3">
      <p className="text-[12px] leading-relaxed text-faint">
        A cadence generates every iteration from a start day and a length. A one-off with its own
        dates replaces the generated iteration it lands on, and the numbering carries on past it.
        Changes save themselves a moment after you stop typing.
      </p>

      {cadence ? (
        <div className="flex flex-col gap-2 rounded-lg border border-line bg-bg p-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={cadence.name}
              onChange={(event) =>
                setDraft({ ...draft, cadence: { ...cadence, name: event.target.value } })
              }
              aria-label="Iteration name"
              placeholder="Iteration"
              className="w-28 rounded-md border border-line bg-surface px-2 py-1 text-[13px] outline-none focus:border-line-strong"
            />
            <label className="flex items-center gap-1.5 text-[12px] text-dim">
              starts
              <input
                type="date"
                value={cadence.startDay}
                onChange={(event) =>
                  setDraft({ ...draft, cadence: { ...cadence, startDay: event.target.value } })
                }
                className="rounded-md border border-line bg-surface px-2 py-1 text-[13px] tnum outline-none focus:border-line-strong"
              />
            </label>
            <label className="flex items-center gap-1.5 text-[12px] text-dim">
              every
              <input
                type="number"
                min={1}
                max={MAX_ITERATION_LENGTH}
                value={cadence.lengthDays}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    cadence: { ...cadence, lengthDays: Number(event.target.value) },
                  })
                }
                className="w-16 rounded-md border border-line bg-surface px-2 py-1 text-[13px] tnum outline-none focus:border-line-strong"
              />
              days
            </label>
            <label className="flex items-center gap-1.5 text-[12px] text-dim">
              <input
                type="checkbox"
                checked={cadence.repeat}
                onChange={(event) =>
                  setDraft({ ...draft, cadence: { ...cadence, repeat: event.target.checked } })
                }
              />
              repeats
            </label>
            <button
              type="button"
              onClick={() => setDraft({ ...draft, cadence: null })}
              className="ml-auto rounded-md border border-line px-2 py-1 text-[12px] text-faint transition-colors duration-150 hover:border-danger/40 hover:text-danger"
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() =>
            setDraft({
              ...draft,
              cadence: { name: 'Iteration', startDay: today, lengthDays: 14, repeat: true },
            })
          }
          className="self-start rounded-md border border-line-strong px-2.5 py-1 text-[13px] font-medium text-dim transition-colors duration-150 hover:text-text"
        >
          Add a repeating cadence
        </button>
      )}

      <div className="flex flex-col gap-1.5">
        {draft.custom.map((entry, index) => (
          <div key={entry.id} className="flex flex-wrap items-center gap-2">
            <input
              value={entry.name}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  custom: draft.custom.map((candidate, i) =>
                    i === index ? { ...candidate, name: event.target.value } : candidate,
                  ),
                })
              }
              aria-label="Iteration name"
              className="w-28 rounded-md border border-line bg-bg px-2 py-1 text-[13px] outline-none focus:border-line-strong"
            />
            <input
              type="date"
              value={entry.startDay}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  custom: draft.custom.map((candidate, i) =>
                    i === index ? { ...candidate, startDay: event.target.value } : candidate,
                  ),
                })
              }
              aria-label="Starts"
              className="rounded-md border border-line bg-bg px-2 py-1 text-[13px] tnum outline-none focus:border-line-strong"
            />
            <input
              type="date"
              value={entry.endDay}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  custom: draft.custom.map((candidate, i) =>
                    i === index ? { ...candidate, endDay: event.target.value } : candidate,
                  ),
                })
              }
              aria-label="Ends"
              className="rounded-md border border-line bg-bg px-2 py-1 text-[13px] tnum outline-none focus:border-line-strong"
            />
            <button
              type="button"
              onClick={() =>
                setDraft({ ...draft, custom: draft.custom.filter((_, i) => i !== index) })
              }
              aria-label={`Remove ${entry.name}`}
              className="rounded-md border border-line px-2 py-1 text-[12px] text-faint transition-colors duration-150 hover:border-danger/40 hover:text-danger"
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() =>
            setDraft({
              ...draft,
              custom: [
                ...draft.custom,
                {
                  id: newIterationId(new Set(draft.custom.map((entry) => entry.id))),
                  name: 'One-off',
                  startDay: today,
                  endDay: shiftDayKey(today, 6),
                },
              ],
            })
          }
          className="rounded-md border border-line px-2.5 py-1 text-[13px] font-medium text-dim transition-colors duration-150 hover:border-line-strong hover:text-text"
        >
          Add a one-off
        </button>
        <span className="ml-auto text-right" role="status" aria-live="polite">
          <SaveStatus state={status.state} message={status.message} />
        </span>
      </div>

      {preview.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5 border-t border-line pt-2.5">
          {preview.map((iteration) => (
            <li
              key={iteration.id}
              className="rounded border border-line px-1.5 py-0.5 text-[11px] text-faint tnum"
            >
              {iteration.name} · {formatDayLabel(iteration.startDay, 'd MMM')}–
              {formatDayLabel(iteration.endDay, 'd MMM')}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
